"use client";

import React from "react";
import { IoHourglassOutline } from "react-icons/io5";
import { TbAward } from "react-icons/tb";
import SnappedWidth from "~/components/ui/snapped-width";
import RowPoints from "~/components/detailed-stats/row-points";
import { formatPercentage } from "~/lib/detailedStats";
import formatPercent, {
  formatDualPercent,
  formatRatingValue,
  formatSignedRatingValue,
} from "~/lib/formatPercent";

type Props = {
  totalCount: number;
  uncompletedCount: number;
  isSolvedOnlyMode: boolean;
  denominator: number;
  percentAll?: number;
  percentSolved?: number;
  earnedPointsAll?: number;
  maxPointsAll?: number;
  earnedPointsSolved?: number;
  maxPointsSolved?: number;
  scurr?: number;
  smax?: number;
  smaxSolved?: number;
  scurrSolved?: number;
  hasSolvedProblems?: boolean;
};

export default function StatsSummary({
  totalCount,
  uncompletedCount,
  isSolvedOnlyMode,
  denominator,
  percentAll = 0,
  percentSolved = 0,
  earnedPointsAll,
  maxPointsAll,
  earnedPointsSolved,
  maxPointsSolved,
  scurr = 0,
  smax = 0,
  smaxSolved = 0,
  scurrSolved = 0,
  hasSolvedProblems = false,
}: Props) {
  const earnedPointsAllValue = earnedPointsAll ?? scurr ?? 0;
  const maxPointsAllValue = maxPointsAll ?? smax ?? 0;
  const earnedPointsSolvedValue =
    earnedPointsSolved ?? scurrSolved ?? earnedPointsAllValue;
  const maxPointsSolvedValue = maxPointsSolved ?? smaxSolved ?? maxPointsAllValue;

  const percentText = isSolvedOnlyMode
    ? hasSolvedProblems
      ? formatPercent(percentSolved ?? 0, 100)
      : "-"
    : formatDualPercent(
        percentAll ?? 0,
        percentSolved ?? 0,
        hasSolvedProblems ?? false,
      );

  const percentPointsEarned = isSolvedOnlyMode
    ? earnedPointsSolvedValue
    : earnedPointsAllValue;
  const percentPointsMax = isSolvedOnlyMode
    ? maxPointsSolvedValue
    : maxPointsAllValue;
  const showPercentPoints = hasSolvedProblems;
  const showRatingRow = smax > 0;
  const ratingValueClassName = (() => {
    const rounded = Math.round(scurr * 10) / 10;

    if (rounded > 0) return "text-green-500";
    if (rounded < 0) return "text-red-500";
    return undefined;
  })();
  const ratingSecondaryText = hasSolvedProblems
    ? ` из ${formatRatingValue(smax)} (${formatRatingValue(smaxSolved)})`
    : ` из ${formatRatingValue(smax)}`;

  return (
    <>
      <div className="mb-3">
        <div className="grid grid-cols-[auto_1fr] items-start gap-4">
          <SnappedWidth className="flex items-start gap-2">
            <span className="font-medium"># :</span>
          </SnappedWidth>
          <div className="">{totalCount}</div>
        </div>
      </div>

      <div className="mb-3">
        <div className="grid grid-cols-[auto_1fr] items-start gap-4">
          <SnappedWidth className="flex items-start gap-1">
            <IoHourglassOutline data-testid="hourglass-icon" className="mt-1" />
            <span>:</span>
          </SnappedWidth>
          <div className="flex items-start gap-2">
            {uncompletedCount > 0 ? (
              <>
                <span>{uncompletedCount}</span>
                {!isSolvedOnlyMode && (
                  <span>
                    ({formatPercentage(uncompletedCount, denominator)}%)
                  </span>
                )}
              </>
            ) : (
              "-"
            )}
          </div>
        </div>
      </div>

      <div className="mb-3">
        <div className="grid grid-cols-[auto_1fr] items-start gap-4">
          <SnappedWidth className="flex items-start gap-2">
            <span className="font-medium">% :</span>
          </SnappedWidth>
          <div className="flex items-start gap-2">
            <span>{percentText}</span>
            {showPercentPoints && (
              <RowPoints
                earnedPoints={percentPointsEarned}
                maxPoints={percentPointsMax}
              />
            )}
          </div>
        </div>
      </div>

      {showRatingRow && (
        <div className="mb-3">
          <div className="grid grid-cols-[auto_1fr] items-start gap-4">
            <SnappedWidth className="flex items-start gap-2">
              <TbAward data-testid="rating-icon" className="mt-1" />
              <span>:</span>
            </SnappedWidth>
            <div className="flex items-start gap-2">
              <span className="text-muted-foreground">
                <span className={ratingValueClassName}>
                  {formatSignedRatingValue(scurr)}
                </span>
                <span>{ratingSecondaryText}</span>
              </span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
